import { cn } from "@/lib/cn";
import { toneClasses, toneDotClass } from "@/components/ui/badgeTones";

export type BadgeVariant =
  | "draft"
  | "planned"
  | "in_progress"
  | "completed"
  | "failed"
  | "pending";

interface Props {
  variant: BadgeVariant;
  label: string;
  className?: string;
}

// TASK-5133: each variant is a badge TONE (DEV_RULES §10.10) — the pill from
// the tone tokens, and the dot in the SAME tone's fg colour (it used to be
// `brand-primary` on the blue `planned` pill — the dealer's orange inside a
// blue status, a Sprint 13 leftover from before white-label existed). `pending` was
// yellow-100/800 and is now the warning tone (amber), visually adjacent.
const variantStyles: Record<BadgeVariant, { container: string; dot: string }> =
  {
    draft: {
      container: toneClasses("neutral"),
      dot: toneDotClass("neutral"),
    },
    planned: {
      container: toneClasses("info"),
      dot: toneDotClass("info"),
    },
    in_progress: {
      container: toneClasses("progress"),
      dot: toneDotClass("progress"),
    },
    completed: {
      container: toneClasses("success"),
      dot: toneDotClass("success"),
    },
    failed: {
      container: toneClasses("danger"),
      dot: toneDotClass("danger"),
    },
    pending: {
      container: toneClasses("warning"),
      dot: toneDotClass("warning"),
    },
  };

export function Badge({ variant, label, className }: Props) {
  const styles = variantStyles[variant];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium",
        styles.container,
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", styles.dot)} />
      {label}
    </span>
  );
}
